import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputBase from '@material-ui/core/InputBase';
import { withStyles } from '@material-ui/core';
import Checkbox from './Checkbox';
import {
  getMenuProps,
  getArrowClasses,
  getInputClasses,
  getMenuItemClasses,
  styles
} from './Dropdown/common-styles';
import styled from 'styled-components';
import { SIZES } from 'common/constants/sizes';
import { getColor } from 'common/constants/_colors';

/* istanbul ignore next */
const StyledInput = styled(InputBase)`
  ${({ isWithReactForm, height, fontSize, selected }) =>
    isWithReactForm
      ? `> div {
    box-sizing: border-box;
    ${height ? `height: ${height};` : ''}
    ${fontSize ? `font-size: ${SIZES[fontSize]};` : ''};
    ${selected ? '' : `color: ${getColor('osloGray')};`};
  }`
      : ''}
`;
const Dropdown = ({
  options,
  selectedValue = null,
  placeholder,
  classes,
  width,
  dropdownStyles = {},
  height,
  menuHeight,
  multiple,
  multipleLimit,
  disabled,
  inputProps = {},
  menuProps = {},
  onChange = _.noop,
  renderSelected,
  fontSize,
  shouldStopPropagationOnMouseLeave = true,
  open = false,
  onOpen = null,
  onClose = null,
  dataCy = null,
  isWithReactForm = false,
  ...rest
}) => {
  const [selected, setSelected] = useState(
    selectedValue || (multiple ? [] : '')
  );
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    setSelected(selectedValue || (multiple ? [] : ''));
  }, [selectedValue, multiple]);

  useEffect(() => setIsOpen(open), [open]);

  const renderValue = value => {
    if (_.isEmpty(String(value))) {
      return placeholder;
    }

    if (renderSelected) {
      return renderSelected(value);
    } else {
      return options
        .filter(option =>
          (_.isArray(value) ? value : [value]).includes(option.value)
        )
        .map(({ displayValue }) => displayValue);
    }
  };

  const handleOnChange = event => {
    const { value } = event.target;
    if (isWithReactForm) {
      onChange(event);
      setSelected(value);
      return;
    }
    if (
      multiple &&
      ((!isSelectAll && value.includes('all')) ||
        (isSelectAll && !value.includes('all')))
    ) {
      handleOnAllChange();
    } else {
      if (isSelectAll && value.includes('all')) {
        _.remove(value, val => val === 'all');
        setIsSelectAll(false);
      }
      onChange(value);
      setSelected(value);
    }
  };

  const handleOnAllChange = () => {
    const newIsSelectAll = !isSelectAll;
    setIsSelectAll(newIsSelectAll);
    const returnValues = newIsSelectAll
      ? options.map(option => option.value)
      : [];
    onChange(returnValues.filter(val => val !== 'all'));
    setSelected(returnValues);
  };
  const handleClose = () => (onClose ? onClose() : setIsOpen(false));
  const handleOpen = () => (onOpen ? onOpen() : setIsOpen(true));

  const isMaxLimit =
    multiple && multipleLimit && _.size(selectedValue) === multipleLimit;

  const handleMouseLeave = e => {
    if (shouldStopPropagationOnMouseLeave) {
      e.stopPropagation();
    }
  };

  return (
    <Select
      style={dropdownStyles}
      id='select'
      displayEmpty
      data-cy={dataCy}
      multiple={multiple}
      value={selected}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      onMouseLeave={handleMouseLeave}
      IconComponent={() => getArrowClasses({ isOpen, classes, disabled })}
      onChange={handleOnChange}
      renderValue={renderValue}
      MenuProps={{
        ...getMenuProps({ menuHeight })
      }}
      input={
        <StyledInput
          {...(isWithReactForm && { isWithReactForm })}
          selected={selected}
          fontSize={fontSize}
          height={height}
          style={{
            ...(width === 'auto' ? {} : { width }),
            ...(height === 'auto' ? {} : { height })
          }}
          classes={{
            ...getInputClasses(width, classes, isOpen, inputProps),
            focused: classes.focused
          }}
          {...(isWithReactForm ? inputProps : {})}
        />
      }
      disabled={disabled}
      {...rest}
    >
      {options.map((option, idx) => {
        const isChecked = multiple && selected.indexOf(option.value) > -1;
        const isDisabled =
          option.isDisabled ||
          (isMaxLimit && !isChecked && !option.ignoreMaxLimit);

        return (
          <MenuItem
            disableRipple
            key={idx}
            data-cy={`${dataCy}-item-${
              option?.value ? option.value : option?.displayValue
            }`}
            value={option.value}
            disableGutters={multiple}
            classes={{
              ...getMenuItemClasses({
                menuProps,
                classes,
                multiple
              })
            }}
            disabled={isDisabled}
          >
            {multiple && <Checkbox checked={isChecked} />}
            {option.displayValue}
          </MenuItem>
        );
      })}
    </Select>
  );
};

Dropdown.propTypes = {
  classes: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  menuHeight: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  selectedValue: PropTypes.any,
  inputProps: PropTypes.object,
  open: PropTypes.bool
};

export default withStyles(styles)(Dropdown);
